/**
 * Pure rules of the MultiSelect additions (M27 F1-F3 T3): umlaut-tolerant search, the "N gewählt" summary, quick filters and
 * Alle/Keine over the FILTERED rows. No React, no DOM — tested in __tests__/multiSelectSuche.test.ts.
 */

/** Both spellings of a German umlaut/ß reach the same text: "Mühle", "Muhle" and "Muehle" are found by each other. */
const ohneAkzent = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '')
const klein = (s: string) => s.toLowerCase().replace(/ß/g, 'ss')

/** ä->a, ö->o, ü->u (typed without the dots). */
export const foldKurz = (s: string): string => ohneAkzent(klein(s)).replace(/\s+/g, ' ').trim()
/** ä->ae, ö->oe, ü->ue (typed as the keyboard-free spelling). */
export const foldLang = (s: string): string =>
  klein(s).replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/\s+/g, ' ').trim()

/** Does `text` contain `query`, ignoring case, umlaut spelling and ß? An empty query matches everything. */
export function passtSuche(text: string, query: string): boolean {
  const kurz = foldKurz(query)
  if (!kurz) return true
  return foldKurz(text).includes(kurz) || foldLang(text).includes(foldLang(query))
}

export interface SuchOption { value: string; label: string; note?: string; disabled?: boolean; tags?: readonly string[] }

/** Rows after the search AND the quick filters (a quick filter is a tag; several pressed tags = union; none pressed = no narrowing). */
export function gefiltert<T extends SuchOption>(options: readonly T[], query: string, schnell: ReadonlySet<string> = new Set()): T[] {
  return options.filter((o) => (schnell.size === 0 || (o.tags ?? []).some((t) => schnell.has(t))) && (passtSuche(o.label, query) || (o.note ? passtSuche(o.note, query) : false)))
}

/** From `ab` selections (default 4) the trigger shows ONE summary ("12 Schulen gewählt") instead of one chip per selection. */
export const zeigeSammelchip = (n: number, ab = 4): boolean => n >= ab

/** "Alle": add every selectable filtered row (disabled rows stay out), up to `max` selections in total. Existing selections keep their order. */
export function alleAuswaehlen(selected: readonly string[], zeilen: readonly SuchOption[], max?: number): string[] {
  const out = [...selected]
  const drin = new Set(out)
  for (const z of zeilen) {
    if (z.disabled || drin.has(z.value)) continue
    if (max != null && out.length >= max) break
    out.push(z.value)
    drin.add(z.value)
  }
  return out
}

/** "Keine": remove only the filtered rows from the selection (rows hidden by the search keep their state). */
export function keineAuswaehlen(selected: readonly string[], zeilen: readonly SuchOption[]): string[] {
  const weg = new Set(zeilen.map((z) => z.value))
  return selected.filter((v) => !weg.has(v))
}

/** Are all selectable filtered rows selected? (Alle is then disabled.) */
export const alleGewaehlt = (selected: readonly string[], zeilen: readonly SuchOption[]): boolean => {
  const drin = new Set(selected)
  const frei = zeilen.filter((z) => !z.disabled)
  return frei.length > 0 && frei.every((z) => drin.has(z.value))
}
